import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Grid,
  Select,
  MenuItem,
  useMediaQuery,
  InputBase,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import {
  Search,
  Refresh,
  ConfirmationNumberOutlined,
  CheckCircleOutline,
  PendingOutlined,
  CloseOutlined,
  FilterAltOutlined,
  ClearOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import LoaderScreen from "../ProgressCircle/LoaderScreen";
import ticketService from "../../repository/ticketRepository";

const UserTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const token = sessionStorage.getItem("newToken");

      const response = await ticketService.getUserTickets(token);

      const ticketData = (response.data.ticketData || []).map((ticket) => ({
        id: ticket.ticketId || "N/A",
        userId: ticket.userId || "N/A",
        contact: ticket.contact || "N/A",
        email: ticket.email || "N/A",
        issueType: ticket.issueType || "N/A",
        description: ticket.description || "",
        status: ticket.status || "Open",
        comment: ticket.adminComment || "",
        createdAt: ticket.createdAt
          ? new Date(ticket.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "N/A",
      }));

      setTickets(ticketData);
    } catch (err) {
      setError("Failed to fetch user tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const filteredTickets = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return tickets.filter((ticket) => {
      const matchesSearch =
        !search ||
        String(ticket.id).toLowerCase().includes(search) ||
        String(ticket.userId).toLowerCase().includes(search) ||
        String(ticket.email).toLowerCase().includes(search) ||
        String(ticket.contact).toLowerCase().includes(search) ||
        String(ticket.issueType).toLowerCase().includes(search) ||
        String(ticket.description).toLowerCase().includes(search);

      const matchesStatus =
        filterStatus === "all" ||
        String(ticket.status).toLowerCase() === filterStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [tickets, searchTerm, filterStatus]);

  const openTickets = tickets.filter(
    (ticket) => String(ticket.status).toLowerCase() === "open",
  ).length;

  const closedTickets = tickets.filter(
    (ticket) => String(ticket.status).toLowerCase() === "closed",
  ).length;

  const getStatusConfig = (status) => {
    const normalized = String(status || "")
      .toLowerCase()
      .trim();

    if (normalized === "closed") {
      return {
        label: "Closed",
        color: "success",
        icon: <CheckCircleOutline sx={{ fontSize: 15 }} />,
      };
    }

    if (normalized === "pending" || normalized === "in progress") {
      return {
        label: status,
        color: "warning",
        icon: <PendingOutlined sx={{ fontSize: 15 }} />,
      };
    }

    return {
      label: status || "Open",
      color: "error",
      icon: <ConfirmationNumberOutlined sx={{ fontSize: 15 }} />,
    };
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
  };

  if (loading) {
    return <LoaderScreen />;
  }

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },
        minHeight: "calc(100vh - 70px)",
        backgroundColor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <ConfirmationNumberOutlined
              sx={{
                color: isDark ? "#D1B99A" : "#806B52",
                fontSize: 25,
              }}
            />

            <Typography
              sx={{
                fontSize: {
                  xs: 22,
                  md: 26,
                },
                fontWeight: 700,
                letterSpacing: "-0.4px",
                color: "text.primary",
              }}
            >
              Support Tickets
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            Manage and monitor customer support requests.
          </Typography>
        </Box>

        <Tooltip title="Refresh tickets">
          <IconButton
            onClick={fetchTickets}
            disabled={loading}
            sx={{
              width: 42,
              height: 42,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <Refresh
              sx={{
                fontSize: 21,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
        }}
      >
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    mb: 0.7,
                  }}
                >
                  Total Tickets
                </Typography>

                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: 700,
                  }}
                >
                  {tickets.length}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark
                    ? "rgba(209,185,154,0.12)"
                    : "rgba(128,107,82,0.10)",
                  color: isDark ? "#D1B99A" : "#806B52",
                }}
              >
                <ConfirmationNumberOutlined />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    mb: 0.7,
                  }}
                >
                  Open Tickets
                </Typography>

                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: 700,
                  }}
                >
                  {openTickets}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(237,108,2,0.10)",
                  color: "#ed6c02",
                }}
              >
                <PendingOutlined />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    mb: 0.7,
                  }}
                >
                  Closed Tickets
                </Typography>

                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: 700,
                  }}
                >
                  {closedTickets}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(46,125,50,0.10)",
                  color: "#2e7d32",
                }}
              >
                <CheckCircleOutline />
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          backgroundColor: "background.paper",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 1.5,
              md: 2,
            },
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  height: 44,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  transition: "all .2s ease",
                  "&:focus-within": {
                    borderColor: isDark ? "#D1B99A" : "#806B52",
                    backgroundColor: "background.paper",
                    boxShadow: isDark
                      ? "0 0 0 3px rgba(209,185,154,.08)"
                      : "0 0 0 3px rgba(128,107,82,.08)",
                  },
                }}
              >
                <Search
                  sx={{
                    ml: 1.5,
                    color: "text.secondary",
                    fontSize: 20,
                  }}
                />

                <InputBase
                  placeholder="Search ticket, user, email, contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    ml: 1,
                    mr: 1,
                    flex: 1,
                    fontSize: 13,
                    color: "text.primary",
                  }}
                />

                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{
                      mr: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <CloseOutlined fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
                sx={{
                  height: 44,
                  borderRadius: 2,
                  fontSize: 13,
                  backgroundColor: "action.hover",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                }}
                startAdornment={
                  <FilterAltOutlined
                    sx={{
                      ml: 1,
                      mr: 0.5,
                      fontSize: 18,
                      color: "text.secondary",
                    }}
                  />
                }
              >
                <MenuItem value="all">All Status</MenuItem>

                <MenuItem value="Open">Open</MenuItem>

                <MenuItem value="Pending">Pending</MenuItem>

                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearOutlined />}
                onClick={clearFilters}
                sx={{
                  height: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 13,
                  borderColor: "divider",
                  color: "text.secondary",
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box
          sx={{
            px: 2,
            py: 1.4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 650,
              }}
            >
              Ticket Records
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                color: "text.secondary",
                mt: 0.2,
              }}
            >
              Showing {filteredTickets.length} of {tickets.length} tickets
            </Typography>
          </Box>

          {(searchTerm || filterStatus !== "all") && (
            <Chip
              size="small"
              label="Filters active"
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                fontSize: 11,
              }}
            />
          )}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: {
              xs: "calc(100vh - 360px)",
              md: "calc(100vh - 330px)",
            },
            minHeight: 300,
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <Table
            stickyHeader
            size={isMobile ? "small" : "medium"}
            sx={{
              minWidth: 1200,
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Ticket ID",
                  "User ID",
                  "Email",
                  "Contact",
                  "Issue Type",
                  "Description",
                  "Status",
                  "Admin Comment",
                  "Created At",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      backgroundColor: isDark ? "#211F1C" : "#F6F2ED",
                      color: "text.secondary",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      py: 1.7,
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      borderBottom: 0,
                      py: 9,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "action.hover",
                          mb: 1.5,
                        }}
                      >
                        <ConfirmationNumberOutlined
                          sx={{
                            color: "text.secondary",
                            fontSize: 26,
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                        }}
                      >
                        No tickets found
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          mt: 0.5,
                        }}
                      >
                        Try changing your search or filter.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => {
                  const status = getStatusConfig(ticket.status);

                  return (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 650,
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {ticket.id}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {ticket.userId}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          borderColor: "divider",
                        }}
                      >
                        <Tooltip title={ticket.email}>
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 210,
                              fontSize: 12.5,
                            }}
                          >
                            {ticket.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {ticket.contact}
                      </TableCell>

                      <TableCell
                        sx={{
                          borderColor: "divider",
                        }}
                      >
                        <Chip
                          label={ticket.issueType}
                          size="small"
                          variant="outlined"
                          sx={{
                            maxWidth: 160,
                            borderRadius: 1.5,
                            fontSize: 11,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 280,
                          borderColor: "divider",
                        }}
                      >
                        <Tooltip title={ticket.description || "No description"}>
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 270,
                              fontSize: 12.5,
                              color: "text.secondary",
                            }}
                          >
                            {ticket.description || "No description"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          borderColor: "divider",
                        }}
                      >
                        <Chip
                          size="small"
                          icon={status.icon}
                          label={status.label}
                          color={status.color}
                          sx={{
                            height: 27,
                            borderRadius: 1.5,
                            fontSize: 11,
                            fontWeight: 600,
                            "& .MuiChip-icon": {
                              fontSize: 15,
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 250,
                          borderColor: "divider",
                        }}
                      >
                        <Tooltip title={ticket.comment || "No admin comment"}>
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 240,
                              fontSize: 12.5,
                              color: ticket.comment
                                ? "text.primary"
                                : "text.disabled",
                              fontStyle: ticket.comment ? "normal" : "italic",
                            }}
                          >
                            {ticket.comment || "No admin comment"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {ticket.createdAt}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserTickets;
